import { alpha, createTheme, type Theme } from '@mui/material/styles';

/// Emiro's own colours, the same ones the website is drawn in. Dark first, because that is what
/// a media interface is looked at in, and because posters and artwork have to carry the colour,
/// not the chrome around them.
const palette = {
  // Near-black neutrals, layered rather than shaded: the ground deepest, raised surfaces above
  // it, cards above those. Depth comes from the layers and hairlines, not from grey overlays.
  abyss: '#040506',
  ground: '#040506',
  groundRaised: '#0A0C0F',
  sidebar: '#0A0C0F',
  canvas: '#040506',
  surface: '#101318',
  surfaceHigh: '#171B22',
  line: 'rgba(255, 255, 255, 0.08)',
  lineStrong: 'rgba(255, 255, 255, 0.16)',

  text: '#F5F6F8',
  textSoft: '#C3C8D0',
  textMuted: '#9AA1AC',

  // The logo's own gradient, from its brightest stop to its deepest. Teal reads as "playing"
  // without borrowing anyone else's identity: Plex is amber, Emby green, Jellyfin purple.
  accent: '#13D8D2',
  accentBright: '#25F0E8',
  accentDim: '#0AA9A7',

  // Warm amber is reserved for one thing: the server had to do work it would rather not,
  // which in this product means transcoding.
  warm: '#F0A24B',
  danger: '#FF6B6F',
};

/// The same interface in daylight. Not the dark one inverted: the neutrals are layered the
/// other way round - the canvas recedes and cards come forward as white - and every colour
/// that carries text is taken down until it holds its own against a white ground. The logo's
/// teal reads as a pastel highlighter on white, so it becomes a deeper one of the same hue.
const lightPalette = {
  ground: '#F3F5F7',
  groundRaised: '#FFFFFF',
  canvas: '#F3F5F7',
  surface: '#FFFFFF',
  surfaceHigh: '#ECEFF3',
  sidebar: '#FFFFFF',
  line: 'rgba(16, 19, 25, 0.09)',
  lineStrong: 'rgba(16, 19, 25, 0.18)',

  text: '#0E1116',
  textSoft: '#2F3540',
  textMuted: '#59616E',

  accent: '#087F7C',
  accentBright: '#066A68',
  accentDim: '#0AA9A7',

  warm: '#A8600C',
  danger: '#C4292E',
};

/// The logo's gradient, for the few things that should look like the mark itself: the primary
/// button, the avatar, a word that deserves the emphasis.
export const brandGradient = `linear-gradient(135deg, ${palette.accentBright} 0%, ${palette.accent} 50%, ${palette.accentDim} 100%)`;

/// White into the brand's teal, for a headline on the dark ground - the website's hero line.
export const headlineGradient = `linear-gradient(120deg, #FFFFFF 0%, #D9FFFD 30%, ${palette.accentBright} 60%, ${palette.accentDim} 100%)`;

/// The motion the website moves in: long, soft landings, and a small overshoot for things that
/// are pressed.
export const easing = {
  out: 'cubic-bezier(0.16, 1, 0.3, 1)',
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
};

/// The two grounds as an rgb triple. A page that lays a wash over artwork - the hero of a
/// detail page - fades it into the ground the page itself stands on, so the picture ends in
/// the page rather than at an edge.
export const groundRgb = { dark: '4, 5, 6', light: '243, 245, 247' } as const;

/// How far the content column stands in from the window's start edge - the floating sidebar,
/// where there is one. Set by the shell; a hero that reaches for the window edge reads it, so its
/// picture runs on under the glass of the sidebar rather than stopping beside it.
export const shellStart = 'var(--emiro-shell-start, 0px)';

/// The floating bar: its inset from the top plus its own height, per breakpoint. A hero that
/// starts up under the bar moves up by this much, plus the page's own top padding.
export const barOffset = { xs: 60, md: 68 } as const;

/// Frosted glass: a surface that lets whatever scrolls under it show through, blurred. Menus,
/// the top bar and dialogs are glass; the content itself is not.
export function glass(mode: 'dark' | 'light', strength: 'thin' | 'thick' = 'thick') {
  const dark = mode === 'dark';

  return {
    backgroundColor: dark
      ? `rgba(16, 19, 24, ${strength === 'thick' ? 0.78 : 0.55})`
      : `rgba(255, 255, 255, ${strength === 'thick' ? 0.84 : 0.66})`,
    backdropFilter: 'blur(24px) saturate(170%)',
    WebkitBackdropFilter: 'blur(24px) saturate(170%)',
  };
}

export function createAppTheme(direction: 'ltr' | 'rtl', mode: 'dark' | 'light' = 'dark'): Theme {
  const dark = mode === 'dark';

  // One place decides what each colour is in this mode; everything below reads from it,
  // rather than asking which mode it is a few dozen times.
  const c = dark ? palette : { ...palette, ...lightPalette };

  // What sits on the teal: near-black on the bright dark-theme teal, white on the deep one
  // daylight needs.
  const onAccent = dark ? '#02100F' : '#FFFFFF';

  // A coloured chip or alert is the colour at low strength with the colour itself for text,
  // the way the website draws its status chips - never a solid slab.
  const tinted = (colour: string, text: string = colour) => ({
    color: text,
    backgroundColor: alpha(colour, dark ? 0.1 : 0.09),
    border: `1px solid ${alpha(colour, dark ? 0.32 : 0.28)}`,
  });

  const shadowDeep = dark
    ? '0 1px 0 rgba(255,255,255,0.08) inset, 0 30px 80px -30px rgba(0,0,0,0.85)'
    : '0 1px 0 rgba(255,255,255,0.9) inset, 0 24px 60px -28px rgba(16,19,25,0.28)';

  return createTheme({
    direction,
    palette: {
      mode,
      primary: {
        main: c.accent,
        light: c.accentBright,
        dark: c.accentDim,
        contrastText: onAccent,
      },
      secondary: { main: c.warm, contrastText: dark ? '#1A1206' : '#FFFFFF' },
      error: { main: c.danger },
      warning: { main: c.warm },
      success: { main: c.accent, contrastText: onAccent },
      background: { default: c.canvas, paper: c.surface },
      text: { primary: c.text, secondary: c.textMuted },
      divider: c.line,
      action: {
        hover: dark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(16, 19, 25, 0.05)',
        selected: alpha(c.accent, dark ? 0.14 : 0.1),
      },
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: [
        'Inter Variable',
        'Inter',
        '-apple-system',
        'BlinkMacSystemFont',
        'SF Pro Display',
        'Segoe UI',
        'Roboto',
        'Noto Sans',
        'Noto Sans Arabic',
        'Noto Sans Bengali',
        'Noto Sans Devanagari',
        'Noto Sans SC',
        'sans-serif',
      ].join(', '),
      // Tight, confident headings; the interface should read as a product, not as a form.
      h1: { fontSize: 'clamp(2rem, 3.2vw, 2.75rem)', fontWeight: 700, letterSpacing: '-0.035em', lineHeight: 1.05 },
      h2: { fontSize: 'clamp(1.625rem, 2.4vw, 2.125rem)', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.1 },
      h3: { fontSize: '1.3125rem', fontWeight: 680, letterSpacing: '-0.02em', lineHeight: 1.2 },
      h4: { fontSize: '1.125rem', fontWeight: 650, letterSpacing: '-0.015em' },
      h5: { fontSize: '1.0625rem', fontWeight: 650, letterSpacing: '-0.01em' },
      h6: { fontSize: '1rem', fontWeight: 650, letterSpacing: '-0.01em' },
      subtitle1: { fontWeight: 600, letterSpacing: '-0.01em' },
      subtitle2: { fontWeight: 620, letterSpacing: '-0.005em' },
      body1: { letterSpacing: '-0.011em' },
      body2: { letterSpacing: '-0.006em' },
      // The website's eyebrow: small, spaced capitals that name what follows.
      overline: { fontSize: '0.6875rem', letterSpacing: '0.14em', fontWeight: 650, lineHeight: 2 },
      button: { textTransform: 'none', fontWeight: 600, letterSpacing: '-0.01em' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Tells the browser which way round its own furniture goes: form controls, the
          // scrollbar where it is not styled here, and the ground behind an overscroll.
          ':root': { colorScheme: mode },
          body: {
            backgroundColor: c.canvas,
            // The website's aura: a glow of the logo's teal over the top of the screen and a
            // cooler one off to the side, fixed so it stays put while the content moves. On
            // white it is kept to a breath, or it reads as a stain rather than as light.
            backgroundImage: dark
              ? 'radial-gradient(900px 520px at 55% -220px, rgba(19, 216, 210, 0.16), transparent 70%),'
                + ' radial-gradient(700px 520px at -10% 25%, rgba(10, 120, 170, 0.10), transparent 70%)'
              : 'radial-gradient(900px 520px at 55% -260px, rgba(19, 216, 210, 0.12), transparent 70%)',
            backgroundAttachment: 'fixed',
            fontFeatureSettings: "'cv11', 'ss01', 'ss03'",
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          '::selection': { background: c.accent, color: onAccent },
          ':focus-visible': { outline: `2px solid ${c.accentBright}`, outlineOffset: 3 },
          // Scrollbars that do not fight the surfaces they sit on.
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-track': { background: 'transparent' },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.16)',
            borderRadius: 8,
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            backgroundColor: dark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.26)',
          },
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '1ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '1ms !important',
            },
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          // MUI lightens paper by elevation; Emiro uses explicit layers and a hairline instead.
          root: {
            backgroundImage: 'none',
            backgroundColor: c.surface,
            border: `1px solid ${c.line}`,
          },
          rounded: { borderRadius: 18 },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            // A tile on the website: the surface falling away into the ground below it.
            backgroundImage: dark ? `linear-gradient(180deg, ${palette.surface}, ${palette.groundRaised})` : 'none',
            transition: `border-color 300ms ease, box-shadow 300ms ease, transform 500ms ${easing.out}`,
            '&:hover': { borderColor: c.lineStrong },
          },
        },
      },
      MuiCardActionArea: {
        styleOverrides: {
          focusHighlight: { backgroundColor: c.accent },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 999,
            paddingInline: 20,
            paddingBlock: 8,
            transition: `transform 400ms ${easing.spring}, background-color 200ms ease, box-shadow 300ms ease,`
              + ' border-color 200ms ease, color 200ms ease',
            '&:active': { transform: 'scale(0.97)' },
          },
          sizeSmall: { paddingInline: 14, paddingBlock: 4 },
          sizeLarge: { paddingInline: 28, paddingBlock: 11, fontSize: '1rem' },
          outlined: {
            borderColor: c.lineStrong,
            color: c.text,
            backgroundColor: dark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(20px) saturate(160%)',
            '&:hover': {
              borderColor: dark ? 'rgba(255,255,255,0.28)' : 'rgba(16,19,25,0.3)',
              backgroundColor: dark ? 'rgba(255,255,255,0.1)' : '#FFFFFF',
            },
          },
        },
        variants: [
          {
            // The website's primary button: the logo's teal as a lit pill, with a highlight
            // along its top edge and a glow under it.
            props: { variant: 'contained', color: 'primary' },
            style: {
              color: onAccent,
              background: dark
                ? `linear-gradient(180deg, ${palette.accentBright}, ${palette.accent} 60%, #10C3BE)`
                : `linear-gradient(180deg, #0A9794, ${lightPalette.accent} 60%, ${lightPalette.accentBright})`,
              boxShadow: dark
                ? '0 0 0 1px rgba(37,240,232,0.3) inset, 0 10px 32px -10px rgba(19,216,210,0.55),'
                  + ' 0 2px 0 rgba(255,255,255,0.35) inset'
                : '0 1px 0 rgba(255,255,255,0.25) inset, 0 10px 28px -12px rgba(8,127,124,0.65)',
              '&:hover': {
                transform: 'translateY(-1px) scale(1.02)',
                boxShadow: dark
                  ? '0 0 0 1px rgba(37,240,232,0.4) inset, 0 16px 48px -8px rgba(19,216,210,0.6),'
                    + ' 0 2px 0 rgba(255,255,255,0.4) inset'
                  : '0 1px 0 rgba(255,255,255,0.3) inset, 0 16px 36px -12px rgba(8,127,124,0.7)',
              },
              '&.Mui-disabled': {
                background: dark ? 'rgba(255,255,255,0.08)' : 'rgba(16,19,25,0.08)',
                boxShadow: 'none',
              },
            },
          },
          {
            props: { variant: 'contained', color: 'error' },
            style: { boxShadow: `0 10px 30px -12px ${alpha(c.danger, 0.7)}` },
          },
          {
            props: { variant: 'text' },
            style: { '&:hover': { backgroundColor: alpha(c.accent, dark ? 0.1 : 0.08) } },
          },
        ],
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: `background-color 200ms ease, color 200ms ease, transform 400ms ${easing.spring}`,
            '&:active': { transform: 'scale(0.92)' },
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            padding: 3,
            gap: 2,
            backgroundColor: dark ? 'rgba(255,255,255,0.05)' : 'rgba(16,19,25,0.05)',
            border: `1px solid ${c.line}`,
          },
          grouped: {
            border: 0,
            borderRadius: '999px !important',
            margin: 0,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            paddingInline: 14,
            color: c.textMuted,
            '&.Mui-selected': {
              color: dark ? '#07090C' : '#FFFFFF',
              backgroundColor: dark ? 'rgba(255,255,255,0.92)' : c.text,
              '&:hover': { backgroundColor: dark ? '#FFFFFF' : c.textSoft },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: 999, letterSpacing: '0.005em' },
          outlined: {
            borderColor: c.lineStrong,
            color: c.textSoft,
            backgroundColor: dark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.6)',
          },
          filled: {
            backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(16,19,25,0.07)',
          },
        },
        variants: [
          { props: { variant: 'filled', color: 'primary' }, style: tinted(c.accent, c.accentBright) },
          { props: { variant: 'filled', color: 'success' }, style: tinted(c.accent, c.accentBright) },
          { props: { variant: 'filled', color: 'secondary' }, style: tinted(c.warm) },
          { props: { variant: 'filled', color: 'warning' }, style: tinted(c.warm) },
          { props: { variant: 'filled', color: 'error' }, style: tinted(c.danger) },
          { props: { variant: 'outlined', color: 'primary' }, style: tinted(c.accent, c.accentBright) },
          { props: { variant: 'outlined', color: 'success' }, style: tinted(c.accent, c.accentBright) },
          { props: { variant: 'outlined', color: 'secondary' }, style: tinted(c.warm) },
          { props: { variant: 'outlined', color: 'warning' }, style: tinted(c.warm) },
          { props: { variant: 'outlined', color: 'error' }, style: tinted(c.danger) },
        ],
      },
      MuiTextField: { defaultProps: { variant: 'filled', fullWidth: true } },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            backgroundColor: dark ? 'rgba(255,255,255,0.045)' : '#FFFFFF',
            border: `1px solid ${c.line}`,
            transition: 'border-color 200ms ease, box-shadow 200ms ease, background-color 200ms ease',
            '&::before, &::after': { display: 'none' },
            '&:hover': {
              backgroundColor: dark ? 'rgba(255,255,255,0.07)' : '#FFFFFF',
              borderColor: c.lineStrong,
            },
            '&.Mui-focused': {
              backgroundColor: dark ? 'rgba(255,255,255,0.07)' : '#FFFFFF',
              borderColor: c.accent,
              boxShadow: `0 0 0 4px ${alpha(c.accent, dark ? 0.16 : 0.14)}`,
            },
            '&.Mui-error': { borderColor: c.danger },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: c.lineStrong },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: c.accent },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            paddingBlock: 8,
            transition: 'background-color 200ms ease, color 200ms ease',
            '&.Mui-selected': {
              backgroundColor: alpha(c.accent, dark ? 0.12 : 0.1),
              color: c.accentBright,
              boxShadow: `inset 0 0 0 1px ${alpha(c.accent, dark ? 0.22 : 0.18)}`,
              '&:hover': { backgroundColor: alpha(c.accent, dark ? 0.18 : 0.14) },
              '& .MuiListItemIcon-root': { color: c.accentBright },
            },
          },
        },
      },
      MuiListItemIcon: { styleOverrides: { root: { minWidth: 38, color: 'inherit', opacity: 0.9 } } },
      MuiMenu: {
        styleOverrides: {
          paper: {
            ...glass(mode),
            borderRadius: 16,
            border: `1px solid ${dark ? 'rgba(255,255,255,0.1)' : c.line}`,
            boxShadow: shadowDeep,
            minWidth: 200,
          },
          list: { padding: 6 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            minHeight: 40,
            fontSize: '0.9375rem',
            fontWeight: 500,
            '&.Mui-selected': {
              backgroundColor: alpha(c.accent, dark ? 0.14 : 0.1),
              color: c.accentBright,
              '& .MuiListItemIcon-root': { color: c.accentBright },
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: { borderRadius: 16, boxShadow: shadowDeep },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: { ...glass(mode), borderRadius: 16, boxShadow: shadowDeep },
          listbox: { padding: 6, '& .MuiAutocomplete-option': { borderRadius: 10 } },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 28,
            backgroundImage: dark
              ? `linear-gradient(180deg, ${palette.surfaceHigh}, ${palette.surface})`
              : 'none',
            border: `1px solid ${dark ? 'rgba(255,255,255,0.1)' : c.line}`,
            boxShadow: shadowDeep,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { fontSize: '1.3125rem', fontWeight: 700, letterSpacing: '-0.02em' } },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            // Not the invisible backdrop a menu keeps behind itself to catch a click outside.
            '&:not(.MuiBackdrop-invisible)': {
              backgroundColor: dark ? 'rgba(2, 3, 4, 0.66)' : 'rgba(14, 17, 22, 0.32)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            borderBottom: '1px solid transparent',
            boxShadow: 'none',
            backgroundImage: 'none',
            transition: 'background-color 400ms ease, border-color 400ms ease, backdrop-filter 400ms ease',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: c.sidebar,
            backgroundImage: dark
              ? `linear-gradient(180deg, rgba(19,216,210,0.05), transparent 220px), linear-gradient(180deg, ${palette.surface}, ${palette.groundRaised})`
              : 'none',
            color: c.text,
            borderInlineEnd: `1px solid ${c.line}`,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: 3,
            backgroundColor: c.accent,
            boxShadow: dark ? `0 0 12px ${alpha(palette.accent, 0.7)}` : 'none',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.9375rem',
            letterSpacing: '-0.01em',
            minHeight: 48,
            '&.Mui-selected': { color: c.text },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 16, alignItems: 'flex-start' },
        },
        variants: [
          { props: { variant: 'standard', severity: 'success' }, style: tinted(c.accent, c.text) },
          { props: { variant: 'standard', severity: 'info' }, style: tinted(dark ? '#5AB8FF' : '#1769AA', c.text) },
          { props: { variant: 'standard', severity: 'warning' }, style: tinted(c.warm, c.text) },
          { props: { variant: 'standard', severity: 'error' }, style: tinted(c.danger, c.text) },
        ],
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            height: 6,
            backgroundColor: dark ? 'rgba(255,255,255,0.1)' : 'rgba(16,19,25,0.1)',
          },
          bar: { borderRadius: 999 },
        },
        variants: [
          {
            props: { color: 'primary' },
            style: { '& .MuiLinearProgress-bar': { backgroundImage: brandGradient } },
          },
        ],
      },
      MuiSwitch: {
        styleOverrides: {
          track: { borderRadius: 999, opacity: dark ? 0.3 : 0.38 },
          switchBase: {
            '&.Mui-checked + .MuiSwitch-track': { opacity: dark ? 0.55 : 0.5 },
          },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true },
        styleOverrides: {
          tooltip: {
            ...glass('dark'),
            backgroundColor: 'rgba(16, 19, 24, 0.92)',
            color: palette.text,
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 10,
            fontSize: '0.8125rem',
            fontWeight: 500,
            padding: '6px 10px',
          },
          arrow: { color: 'rgba(16, 19, 24, 0.92)' },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottomColor: c.line },
          head: {
            color: c.textMuted,
            fontSize: '0.75rem',
            fontWeight: 650,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          },
        },
      },
      MuiDivider: { styleOverrides: { root: { borderColor: c.line } } },
      MuiAvatar: {
        styleOverrides: {
          colorDefault: { backgroundImage: brandGradient, color: '#02100F', fontWeight: 700 },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            '&::before': { display: 'none' },
            '&.Mui-expanded': { margin: 0 },
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: { backgroundColor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(16,19,25,0.07)' },
          rounded: { borderRadius: 14 },
        },
      },
    },
  });
}

export const emiroPalette = palette;
